import React from 'react';
import { motion } from 'framer-motion';
import { WavesIcon, UsersIcon, LifeBuoyIcon, DumbbellIcon, FileTextIcon, IdCardIcon, CreditCardIcon, CheckIcon, ChevronRightIcon, CalendarDaysIcon, MapPinIcon, TriangleAlertIcon, UserIcon, MailIcon, ArrowRightIcon, WrenchIcon, BarChart3Icon, ExternalLinkIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
type Bilingual = {
  en: string;
  fr: string;
};
type Dep = {
  icon: typeof WavesIcon;
  title: Bilingual;
  desc: Bilingual;
};
const EASE = [0.23, 1, 0.32, 1] as const;
const PHOTO_SWIMMER = 'https://cdn.magicpatterns.com/patterns/generated-images/b78882f6-12ee-4544-b668-5f9e6d917737.jpg';
const SOURCE_URL = 'https://www.recmanagement.com/state-of-the-industry';
const reveal = {
  initial: {
    opacity: 0,
    y: 16
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  }
};
const LEFT_DEPS: Dep[] = [{
  icon: WavesIcon,
  title: {
    fr: 'Plateau',
    en: 'Facility'
  },
  desc: {
    fr: 'Piscine disponible',
    en: 'Pool available'
  }
}, {
  icon: UsersIcon,
  title: {
    fr: 'Moniteur',
    en: 'Instructor'
  },
  desc: {
    fr: 'Qualifié et disponible',
    en: 'Qualified and available'
  }
}, {
  icon: LifeBuoyIcon,
  title: {
    fr: 'Sauveteur',
    en: 'Lifeguard'
  },
  desc: {
    fr: 'Disponible',
    en: 'Available'
  }
}, {
  icon: UsersIcon,
  title: {
    fr: 'Capacité',
    en: 'Capacity'
  },
  desc: {
    fr: '24 places',
    en: '24 spots'
  }
}];
const RIGHT_DEPS: Dep[] = [{
  icon: DumbbellIcon,
  title: {
    fr: 'Équipements',
    en: 'Equipment'
  },
  desc: {
    fr: 'Matériel disponible',
    en: 'Gear available'
  }
}, {
  icon: FileTextIcon,
  title: {
    fr: 'Préalables',
    en: 'Prerequisites'
  },
  desc: {
    fr: 'Âge, niveau, formulaire',
    en: 'Age, level, form'
  }
}, {
  icon: IdCardIcon,
  title: {
    fr: 'Accès',
    en: 'Access'
  },
  desc: {
    fr: 'Carte numérique valide',
    en: 'Valid Digital Card'
  }
}, {
  icon: CreditCardIcon,
  title: {
    fr: 'Paiements',
    en: 'Payments'
  },
  desc: {
    fr: 'Inscription payée',
    en: 'Registration paid'
  }
}];
const CHAIN: {
  icon: typeof UserIcon;
  title: Bilingual;
  desc: Bilingual;
}[] = [{
  icon: UserIcon,
  title: {
    fr: 'Moniteur absent',
    en: 'Instructor absent'
  },
  desc: {
    fr: 'ou indisponible',
    en: 'or unavailable'
  }
}, {
  icon: CalendarDaysIcon,
  title: {
    fr: 'Activité affectée',
    en: 'Activity affected'
  },
  desc: {
    fr: 'à déplacer ou annuler',
    en: 'to move or cancel'
  }
}, {
  icon: MailIcon,
  title: {
    fr: 'Participants à prévenir',
    en: 'Participants to notify'
  },
  desc: {
    fr: '(courriel, texto, application)',
    en: '(email, SMS, app)'
  }
}, {
  icon: MapPinIcon,
  title: {
    fr: 'Horaire à ajuster',
    en: 'Schedule to adjust'
  },
  desc: {
    fr: 'plateau, équipement',
    en: 'facility, equipment'
  }
}, {
  icon: UsersIcon,
  title: {
    fr: 'Ressources à réaffecter',
    en: 'Resources to reassign'
  },
  desc: {
    fr: 'autres cours et moniteurs',
    en: 'other classes and instructors'
  }
}, {
  icon: CreditCardIcon,
  title: {
    fr: 'Remboursement',
    en: 'Refund'
  },
  desc: {
    fr: 'éventuel',
    en: 'if applicable'
  }
}];
const STATS: {
  icon: typeof WrenchIcon;
  value: Bilingual;
  text: Bilingual;
}[] = [{
  icon: WrenchIcon,
  value: {
    fr: '54,4 %',
    en: '54.4%'
  },
  text: {
    fr: 'des répondants citent l’entretien des équipements et des installations comme un défi majeur.',
    en: 'of respondents cite equipment and facility maintenance as a major challenge.'
  }
}, {
  icon: UsersIcon,
  value: {
    fr: '53,2 %',
    en: '53.2%'
  },
  text: {
    fr: 'des répondants citent les enjeux de personnel comme un défi majeur.',
    en: 'of respondents cite staffing issues as a major challenge.'
  }
}, {
  icon: BarChart3Icon,
  value: {
    fr: '54,7 %',
    en: '54.7%'
  },
  text: {
    fr: 'des répondants mentionnent les pressions budgétaires comme un facteur qui influe sur leurs activités.',
    en: 'of respondents mention budget pressures as a factor influencing their operations.'
  }
}];

// Connector geometry (viewBox 0–100 on both axes, stretched to the grid)
const ROWS = [12.5, 37.5, 62.5, 87.5];
const CENTER_ANCHORS = [34, 44, 56, 66];
function DepCard({
  dep,
  pick
}: {
  dep: Dep;
  pick: (v: Bilingual) => string;
}) {
  const Icon = dep.icon;
  return <li className="relative z-10 flex items-center gap-3 rounded-2xl border border-[#EEF0F4] bg-white px-3.5 py-3 shadow-[0_6px_18px_rgba(18,18,40,0.05)]" data-id="element-4102" data-mp="rkxakkno7di3">
      <span className="shrink-0 w-10 h-10 rounded-xl bg-[#F1F1FD] flex items-center justify-center" data-id="element-4103" data-mp="10d8r9eszfmkv">
        <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-4104" data-mp="cnmuey031jg1" />
      </span>
      <span className="flex-1 min-w-0" data-id="element-4105" data-mp="8n2yfxr8wlvh">
        <span className="block font-sans text-[14px] font-semibold text-[#121212] leading-tight" data-id="element-4106" data-mp="8odxxldnr7gs">{pick(dep.title)}</span>
        <span className="block font-sans text-[12.5px] text-[#5B6478] leading-snug truncate" data-id="element-4107" data-mp="mjzkllwf5f61">{pick(dep.desc)}</span>
      </span>
      <span className="shrink-0 w-6 h-6 rounded-full bg-[#22A55B] flex items-center justify-center" data-id="element-4108" data-mp="1y0zlv70qv4c7">
        <CheckIcon className="w-3.5 h-3.5 text-white" strokeWidth={3} aria-hidden="true" data-id="element-4109" data-mp="1t2xttwlcbuol" />
      </span>
    </li>;
}
export function LoisirsChallenge() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bilingual) => isFR ? v.fr : v.en;
  const activityCard = <div className="relative z-10 rounded-2xl border border-[#EEF0F4] bg-white overflow-hidden shadow-[0_18px_40px_rgba(18,18,40,0.10)]" data-id="element-4110" data-mp="31zw997qdukb3">
      <img src={PHOTO_SWIMMER} alt={isFR ? 'Enfant nageant dans une piscine municipale' : 'Child swimming in a municipal pool'} className="w-full h-32 object-cover" data-id="element-4111" data-mp="36yzf2ue6suk4" />
      <div className="p-4" data-id="element-4112" data-mp="2jpvbi0iokoo">
        <p className="flex items-center justify-between font-sans text-[16px] font-semibold text-[#121212]" data-id="element-4113" data-mp="2m2bbpmpskz3">
          {isFR ? 'Cours de natation' : 'Swimming class'}
          <ChevronRightIcon className="w-4 h-4 text-[#5B6478]" aria-hidden="true" data-id="element-4114" data-mp="3pvqvmw2cgvkh" />
        </p>
        <ul className="mt-2.5 space-y-2 font-sans text-[13px] text-[#5B6478]" data-id="element-4115" data-mp="hgqf8zcgvbcl">
          <li className="flex items-center gap-2.5" data-id="element-4116" data-mp="m8gpz6fbg14j">
            <CalendarDaysIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-4117" data-mp="ma1nn4wxg06m" />
            {isFR ? 'Mardi 18 h – 19 h' : 'Tuesday 6–7 PM'}
          </li>
          <li className="flex items-center gap-2.5" data-id="element-4118" data-mp="r5vwzratatma">
            <MapPinIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-4119" data-mp="m5hxixna0rzv" />
            {isFR ? 'Piscine municipale' : 'Municipal pool'}
          </li>
          <li className="flex items-center gap-2.5" data-id="element-4120" data-mp="d5crorxqe1nt">
            <UsersIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-4121" data-mp="d64na2rbvze3" />
            {isFR ? '5 à 10 ans · 24 places' : 'Ages 5 to 10 · 24 spots'}
          </li>
        </ul>
      </div>
    </div>;
  return <div className="max-w-[1480px] mx-auto" data-id="element-4122" data-mp="3dfbf6rv8ji8">
      {/* Top: intro + dependency map */}
      <div className="grid grid-cols-1 lg:grid-cols-[42fr_58fr] gap-12 lg:gap-10 items-center" data-id="element-4123" data-mp="3unnsvgs2jxpr">
        <motion.div {...reveal} transition={{
        duration: 0.5,
        ease: EASE
      }} data-id="element-4124" data-mp="3plyg9wp77bzk">
          <p className="font-sans text-xs font-semibold tracking-[0.2em] uppercase text-[#5B6478]" data-id="element-4125" data-mp="3lmfwj49456yb">
            {isFR ? 'Le défi des équipes de loisirs' : 'The challenge for recreation teams'}
          </p>
          <h2 className="font-sans font-semibold text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.05] tracking-[-0.03em] text-[#121212] mt-4" data-id="element-4126" data-mp="2534gcqpsh3lf">
            {isFR ? 'Une activité n’est jamais' : 'An activity is never'}{' '}
            <span className="text-[#4F46E5]" data-id="element-4127" data-mp="2f0smsdwrbzjf">{isFR ? 'seulement une activité.' : 'just an activity.'}</span>
          </h2>
          <p className="font-sans text-lg md:text-xl text-[#5B6478] leading-relaxed mt-7 max-w-xl" data-id="element-4128" data-mp="1me7osuls0p6w">
            {isFR ? 'Derrière chaque inscription, réservation ou cours, vos équipes doivent coordonner des personnes, des plateaux, des équipements, des règles, des accès et des paiements.' : 'Behind every registration, booking or class, your teams have to coordinate people, facilities, equipment, rules, access and payments.'}
          </p>
        </motion.div>

        <motion.div {...reveal} transition={{
        duration: 0.5,
        delay: 0.08,
        ease: EASE
      }} data-id="element-4129" data-mp="3t5s56nc1ky3l">
          {/* Desktop map */}
          <div className="relative hidden md:grid grid-cols-[1fr_minmax(200px,0.95fr)_1fr] gap-x-8 items-center" data-id="element-4130" data-mp="3o78gif62d87j">
            <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" data-id="element-4131" data-mp="61c7004m2is5">
              {ROWS.map((y, i) => <g key={y} data-id="element-4132" data-mp="fwx69qiap4y7" data-mpi={i}>
                  <path d={`M 34 ${y} C 38 ${y}, 38 ${CENTER_ANCHORS[i]}, 41 ${CENTER_ANCHORS[i]}`} fill="none" stroke="#C9CCE8" strokeWidth="1.2" vectorEffect="non-scaling-stroke" data-id="element-4133" data-mp="b0owksmva3yf" />
                  <path d={`M 66 ${y} C 62 ${y}, 62 ${CENTER_ANCHORS[i]}, 59 ${CENTER_ANCHORS[i]}`} fill="none" stroke="#C9CCE8" strokeWidth="1.2" vectorEffect="non-scaling-stroke" data-id="element-4134" data-mp="yp806uuz1sk6" />
                </g>)}
            </svg>
            <ul className="flex flex-col gap-5" data-id="element-4135" data-mp="1i9gpmtvaa6yc">
              {LEFT_DEPS.map(d => <DepCard key={d.title.en} dep={d} pick={pick} data-id="element-4136" data-mp="13e0tmjb580zb" />)}
            </ul>
            {activityCard}
            <ul className="flex flex-col gap-5" data-id="element-4137" data-mp="18f6j889ib84m">
              {RIGHT_DEPS.map(d => <DepCard key={d.title.en} dep={d} pick={pick} data-id="element-4138" data-mp="uk47v5lw4qkf" />)}
            </ul>
          </div>

          {/* Mobile list */}
          <div className="md:hidden" data-id="element-4139" data-mp="kokeapusu3yu">
            {activityCard}
            <ul className="mt-4 grid grid-cols-1 gap-3 border-l-2 border-[#E4E5F7] pl-4 ml-3" data-id="element-4140" data-mp="pmvnqzh6mcgj">
              {[...LEFT_DEPS, ...RIGHT_DEPS].map(d => <DepCard key={d.title.en} dep={d} pick={pick} data-id="element-4141" data-mp="boe35amkpg2u" />)}
            </ul>
          </div>
        </motion.div>
      </div>

      {/* Chain reaction */}
      <motion.div {...reveal} transition={{
      duration: 0.5,
      ease: EASE
    }} className="mt-16 rounded-3xl bg-[#FEF5F4] border border-[#FBE4E1] px-6 py-8 md:px-10 md:py-9" data-id="element-4142" data-mp="2aaycakwu0u7h">
        <div className="flex items-start gap-5" data-id="element-4143" data-mp="21bb8xtkjlebs">
          <TriangleAlertIcon className="w-10 h-10 shrink-0 text-[#E5484D]" strokeWidth={2} aria-hidden="true" data-id="element-4144" data-mp="21cmdhvltaq3y" />
          <div data-id="element-4145" data-mp="25dy4o5kc3je9">
            <h3 className="font-sans text-2xl md:text-[1.9rem] font-semibold tracking-[-0.02em] text-[#121212] leading-tight" data-id="element-4146" data-mp="2t1z9b3ee6foe">
              {isFR ? 'Et il suffit qu’une seule dépendance change.' : 'And it only takes one dependency to change.'}
            </h3>
            <p className="font-sans text-base md:text-lg text-[#5B6478] leading-relaxed mt-2 max-w-3xl" data-id="element-4147" data-mp="2k95gs49xpjsl">
              {isFR ? 'Une absence, un conflit d’horaire ou un équipement manquant peut affecter toute l’activité et entraîner une série d’ajustements pour vos équipes.' : 'An absence, a scheduling conflict or missing equipment can affect the entire activity and trigger a series of adjustments for your teams.'}
            </p>
          </div>
        </div>

        <ol className="mt-8 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 lg:gap-2" data-id="element-4148" data-mp="10cy9tprsqnmh">
          {CHAIN.map(({
          icon: Icon,
          title,
          desc
        }, i) => <React.Fragment key={title.en} data-id="element-4149" data-mp="1a42nmm5qk2fq" data-mpi={i}>
              <li className="flex lg:flex-col items-center gap-4 lg:gap-3 lg:text-center lg:flex-1 min-w-0" data-id="element-4150" data-mp="1a549lloai9c8">
                <span className="shrink-0 w-14 h-14 rounded-full bg-[#FDE2DF] flex items-center justify-center" data-id="element-4151" data-mp="mf9yb8jmcsu3">
                  <Icon className="w-6 h-6 text-[#D93036]" strokeWidth={1.7} aria-hidden="true" data-id="element-4152" data-mp="wbd1mhq7ixim" />
                </span>
                <span data-id="element-4153" data-mp="rca5jms7g1ms">
                  <span className="block font-sans text-[15px] font-semibold text-[#121212]" data-id="element-4154" data-mp="rbqkhx79cx4j">{pick(title)}</span>
                  <span className="block font-sans text-[14px] text-[#5B6478]" data-id="element-4155" data-mp="27oyotxciap1i">{pick(desc)}</span>
                </span>
              </li>
              {i < CHAIN.length - 1 && <li aria-hidden="true" className="flex items-center justify-start lg:justify-center pl-5 lg:pl-0 lg:pt-5" data-id="element-4156" data-mp="29u3q46psvg11">
                  <ArrowRightIcon className="w-4 h-4 text-[#E5484D] rotate-90 lg:rotate-0" strokeWidth={2} data-id="element-4157" data-mp="2es5acuyyple3" />
                </li>}
            </React.Fragment>)}
        </ol>
      </motion.div>

      {/* Stats */}
      <motion.div {...reveal} transition={{
      duration: 0.5,
      ease: EASE
    }} className="mt-8 rounded-3xl bg-[#F7F7FC] px-6 py-8 md:px-10" data-id="element-4158" data-mp="1r4no1eygvfii">
        <ul className="grid grid-cols-1 md:grid-cols-3 md:divide-x divide-[#E3E4EE] gap-8 md:gap-0" data-id="element-4159" data-mp="1w1e25wqpo11a">
          {STATS.map(({
          icon: Icon,
          value,
          text
        }) => <li key={value.en} className="flex items-start gap-5 md:px-8 first:md:pl-0 last:md:pr-0" data-id="element-4160" data-mp="25ud1a8ju39z5">
              <span className="shrink-0 w-14 h-14 rounded-full bg-[#ECECFB] flex items-center justify-center" data-id="element-4161" data-mp="18cuqd78gd64g">
                <Icon className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-4162" data-mp="1dbfuhhqts9y6" />
              </span>
              <span data-id="element-4163" data-mp="3qmh71u8boelu">
                <span className="block font-sans text-4xl md:text-5xl font-semibold tracking-[-0.03em] text-[#4F46E5]" data-id="element-4164" data-mp="3mlp0j8qu4nt9">
                  {pick(value)}
                </span>
                <span className="block font-sans text-base text-[#5B6478] leading-snug mt-2" data-id="element-4165" data-mp="3hkr9s7rrelg5">{pick(text)}</span>
              </span>
            </li>)}
        </ul>
      </motion.div>
      <p className="mt-4 font-sans text-xs text-[#5B6478] leading-relaxed" data-id="element-4166" data-mp="37pzcz66042mf">
        {isFR ? 'Source : ' : 'Source: '}
        <a href={SOURCE_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 underline decoration-[#C9CCE8] underline-offset-2 hover:text-[#4F46E5] transition-colors duration-150" data-id="element-4167" data-mp="32vldarkb39by">
          Recreation Management, State of the Industry 2026
          <ExternalLinkIcon className="w-3 h-3" aria-hidden="true" data-id="element-4168" data-mp="2jz3ooj0r7pjx" />
        </a>
        {isFR ? '. Enquête auprès de 1 016 professionnels des parcs, loisirs, sports et installations.' : '. Survey of 1,016 parks, recreation, sports and facility professionals.'}
      </p>
    </div>;
}